import React, { useEffect, useState } from 'react';
import { FiFileText, FiAlertCircle } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { applyLateArrival } from '../../Redux/Slice/LeaveForm';
import { findJuniorDevelopers } from '../../Redux/Slice/TaskMasterSlice';
import ConfirmModal from '../../Components/ConfirmModal/ConfirmModal';
import Loader from '../../Components/Loader/Loader';

const LateArrival = ({ onBack }) => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.LeaveForm);
    const { JuniorDevelopers } = useSelector((state) => state.TaskMaster);

    const loginDataStr = Cookies.get("loginData");
    const loginData = loginDataStr ? JSON.parse(loginDataStr) : null;
    const applicantUser = loginData?.data || loginData?.user || {};
    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        dispatch(findJuniorDevelopers());
    }, [dispatch]);

    const [date, setDate] = useState('');
    const [arrivalTime, setArrivalTime] = useState('');
    const [reason, setReason] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [reasonError, setReasonError] = useState(false);
    const [timeError, setTimeError] = useState(false);
    const [dateError, setDateError] = useState(false);

    if (loading) {
        return <Loader />;
    }

    const juniorOptions =
        JuniorDevelopers?.data
            ?.filter((junior) => junior?._id !== applicantUser?._id)
            ?.map((junior) => ({
                label: junior?.Name,
                Role: junior?.Role,
                _id: junior?._id,
            })) || [];

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!date) {
            setDateError(true);
            return;
        }

        if (!arrivalTime || arrivalTime < "10:00" || arrivalTime > "12:00") {
            setTimeError(true);
            return;
        }

        if (!reason.trim()) {
            setReasonError(true);
            return;
        }

        let firstApprover = {
            position: "Senior Developer",
            name: "Pending Assignment",
            status: "Pending"
        };

        const currentRole = applicantUser?.Role || "";

        if (currentRole.toLowerCase().includes("delphi")) {
            const targetApprover = juniorOptions.find(opt =>
                opt.Role === "Delphi Senior Developer" &&
                opt.label && opt.label.toUpperCase().includes("KALPESH")
            ) || juniorOptions.find(opt => opt.Role === "Delphi Senior Developer");

            firstApprover = {
                position: "Delphi Senior Developer",
                name: targetApprover ? targetApprover.label : "KALPESH DILKHUSH",
                approverId: targetApprover ? targetApprover._id : "682c63fe6b35012c7f3a7dd7",
                status: "Pending"
            };
        } else if (currentRole.toLowerCase().includes("web")) {
            const targetApprover = juniorOptions.find(opt =>
                opt.Role === "Web Senior Developer" &&
                opt.label && opt.label.toUpperCase().includes("JAY")
            ) || juniorOptions.find(opt => opt.Role === "Web Senior Developer");

            firstApprover = {
                position: "Web Senior Developer",
                name: targetApprover ? targetApprover.label : "JAY PARMAR",
                approverId: targetApprover ? targetApprover._id : "676bcd353d488c3eeca8291d",
                status: "Pending"
            };
        } else if (currentRole.toLowerCase().includes("customer support")) {
            const targetApprover = juniorOptions.find(opt =>
                opt.Role === "Senior Customer Support" &&
                opt.label && opt.label.toUpperCase().includes("VIPUL")
            ) || juniorOptions.find(opt => opt.Role === "Senior Customer Support");

            firstApprover = {
                position: "Senior Customer Support",
                name: targetApprover ? targetApprover.label : "VIPUL KANTHARIA",
                approverId: targetApprover ? targetApprover._id : "672b4e1df07347a35c340dfa",
                status: "Pending"
            };
        } else if (currentRole === "Data Analyst" || applicantUser.Role === "inhouse") {
            // Data Analyst or inhouse: skip firstApprover, go directly to DIPAK RAJPUT → owner
            const approvers = [
                {
                    name: "DIPAK RAJPUT",
                    position: "Delphi Senior Developer",
                    approverId: "66f7aac08bd6fc731ee1d02f",
                    status: "Pending"
                },
                {
                    position: "owner",
                    name: "GOPESH PERIWAL" || "LALIT PERIWAL",
                    status: "Pending"
                },
            ];

            const payload = {
                applicantId: applicantUser._id,
                applicantName: applicantUser.Name,
                applicantRole: applicantUser.Role,
                date,
                arrivalTime,
                reason,
                approvers
            };

            setPendingPayload(payload);
            setShowConfirmModal(true);
            return; // exit early, skip the default approvers block below
        }

        const approvers = [
            firstApprover,
            {
                name: "DIPAK RAJPUT",
                position: "Delphi Senior Developer",
                approverId: "66f7aac08bd6fc731ee1d02f",
                status: "Pending"
            },
            {
                position: "owner",
                name: "GOPESH PERIWAL" || "LALIT PERIWAL",
                status: "Pending"
            },
        ];

        const payload = {
            applicantId: applicantUser._id,
            applicantName: applicantUser.Name,
            applicantRole: applicantUser.Role,
            date,
            arrivalTime,
            reason,
            approvers
        };

        // Store payload and open confirm modal
        setPendingPayload(payload);
        setShowConfirmModal(true);
    };

    const confirmSubmit = async () => {
        if (!pendingPayload) return;
        setShowConfirmModal(false);
        try {
            const result = await dispatch(applyLateArrival(pendingPayload)).unwrap();
            if (result.status === "Success") {
                toast.success("Late arrival request submitted successfully!", { position: "top-right", autoClose: 3000 });
                onBack();
            }
        } catch (err) {
            toast.error(err || "Failed to submit request.", { position: "top-right", autoClose: 3000 });
        } finally {
            setPendingPayload(null);
        }
    };

    return (
        <>
            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => { setShowConfirmModal(false); setPendingPayload(null); }}
                onConfirm={confirmSubmit}
                title="Submit Late Arrival"
                message="Are you sure you want to submit this late arrival request?"
            />
            <div className="w-full bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100 p-4 md:p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 rounded-full -mr-16 -mt-16 -z-0"></div>
                
                <div className="flex flex-col sm:flex-row justify-between items-start mb-10 gap-4 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-[#133A69] text-white flex items-center justify-center shadow-lg shadow-blue-900/20 px-4 group">
                            <FiFileText className="w-6 h-6 group-hover:scale-110 transition-transform" />
                        </div>
                        <div>
                            <h2 className="text-[20px] font-black text-gray-800 tracking-tight">Late Arrival</h2>
                            <p className="text-[13px] text-gray-500 mt-0.5 font-medium">Request late entry log</p>
                        </div>
                    </div>
                    <button
                        onClick={onBack}
                        type="button"
                        className="text-[12px] font-bold text-[#133A69] hover:bg-blue-50 border-2 border-[#133A69]/10 px-4 py-2 rounded-xl transition-all active:scale-95"
                    >
                        Change Type
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-2">
                            <label className="block text-[13px] font-bold text-gray-700 ml-1">Date</label>
                            <input
                                type="date"
                                value={date}
                                min={today}
                                onChange={(e) => {
                                    setDate(e.target.value);
                                    setDateError(false);
                                }}
                                className={`w-full bg-[#f8fafc] border-2 ${dateError ? 'border-red-400 ring-4 ring-red-50' : 'border-gray-100 focus:border-[#133A69] focus:ring-4 focus:ring-blue-50'} rounded-2xl px-5 py-4 text-sm outline-none text-gray-700 transition-all font-semibold shadow-sm`}
                            />
                            {dateError && (
                                <p className="text-[11px] text-red-500 font-bold mt-2 ml-1 flex items-center gap-1.5 animate-in slide-in-from-left-2">
                                    <FiAlertCircle className="w-3.5 h-3.5" />
                                    Please select a valid date.
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="block text-[13px] font-bold text-gray-700 ml-1">Expected Arrival Time</label>
                            <input
                                type="time"
                                value={arrivalTime}
                                min="10:00"
                                max="12:00"
                                onChange={(e) => {
                                    setArrivalTime(e.target.value);
                                    setTimeError(false);
                                }}
                                className={`w-full bg-[#f8fafc] border-2 ${timeError ? 'border-red-400 ring-4 ring-red-50' : 'border-gray-100 focus:border-[#133A69] focus:ring-4 focus:ring-blue-50'} rounded-2xl px-5 py-4 text-sm outline-none text-gray-700 transition-all font-semibold shadow-sm`}
                            />
                            {timeError && (
                                <p className="text-[11px] text-red-500 font-bold mt-2 ml-1 flex items-center gap-1.5">
                                    <FiAlertCircle className="w-3.5 h-3.5" />
                                    Select time between 10:00 AM and 12:00 PM.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="bg-[#f0f4ff] border-2 border-blue-50 rounded-2xl p-6 flex justify-between items-center group hover:bg-blue-600 transition-all duration-500">
                        <div>
                            <p className="text-[12px] font-black text-blue-600 mb-1 group-hover:text-blue-100 transition-colors uppercase tracking-widest">Shift Schedule</p>
                            <p className="text-[18px] font-black text-gray-800 group-hover:text-white transition-colors">Standard Shift Starts at 10:00 AM</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <FiAlertCircle className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-[13px] font-bold text-gray-700 ml-1">Reason for Late Arrival</label>
                        <textarea
                            value={reason}
                            onChange={(e) => {
                                setReason(e.target.value);
                                if (e.target.value.trim().length > 0) setReasonError(false);
                            }}
                            placeholder="Please provide a brief reason..."
                            className={`w-full bg-[#f8fafc] border-2 ${reasonError ? 'border-red-400 ring-4 ring-red-50' : 'border-gray-100 focus:border-[#133A69] focus:ring-4 focus:ring-blue-50'} rounded-2xl px-5 py-4 text-sm outline-none text-gray-700 transition-all font-semibold shadow-sm min-h-[120px] resize-y`}
                        ></textarea>
                        {reasonError && (
                            <p className="text-[11px] text-red-500 font-bold mt-2 ml-1 flex items-center gap-1.5">
                                <FiAlertCircle className="w-3.5 h-3.5" />
                                Please specify the reason for arriving late.
                            </p>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4 pt-6">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-[2] w-full bg-[#133A69] hover:bg-[#0c2545] text-white font-black py-4 px-8 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl shadow-blue-900/10 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed group"
                        >
                            <span>Submit Application</span>
                            <FiFileText className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            type="button"
                            onClick={onBack}
                            className="flex-1 w-full px-8 py-4 bg-white border-2 border-gray-100 text-gray-500 font-bold rounded-2xl hover:bg-gray-50 hover:text-gray-700 transition-all active:scale-[0.98]"
                        >
                            Discard
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default LateArrival;
